import type {
  SupportTicketOwn,
  SupportTicketType,
} from '@support/entities/support-ticket';
import { useAuthData } from '@auth/features/auth';
import {
  useOwnTicketEditWindow,
  useUpdateOwnTicket,
} from '@support/entities/support-ticket';
import { useEffect, useRef, useState } from 'react';
import {
  Button,
  Input,
  Modal,
  ModalFooter,
  ModalHeader,
  Select,
  Text,
  Textarea,
  toastStore,
} from '@/shared/ui';
import { formatEditTimeLeft } from '../lib/format-edit-time-left';
import { useTicketAttachments } from '../model/use-ticket-attachments';
import { AttachmentsField } from './attachments-field';
import css from './create-support-ticket-modal.module.scss';
import { Required, TYPE_OPTIONS } from './form-parts';

type Props = { ticket: SupportTicketOwn; open: boolean; onClose: () => void };

// The reporter's own-ticket editor, open only inside the short grace window
// after filing (see SUPPORT_OWN_EDIT_WINDOW_MINUTES). Same fields as the create
// form minus the page context, which records where the ticket was raised and
// so is never rewritten.
export const EditSupportTicketModal = ({ ticket, open, onClose }: Props) => {
  const { data: authData } = useAuthData();
  const uploadsEnabled = authData?.uploadsEnabled ?? false;
  const updateTicket = useUpdateOwnTicket();
  const msLeft = useOwnTicketEditWindow(ticket);
  // `isPending` only flips on the next render, so a fast double-click would
  // otherwise send the save twice.
  const savingRef = useRef(false);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('');
  const {
    attachments,
    setAttachments,
    isUploading,
    uploadValidFiles,
    removeAttachment,
    onPaste,
  } = useTicketAttachments(uploadsEnabled);

  // Seed on open only — not on every list refetch mid-edit — so a cancelled
  // draft never lingers and a background refetch never wipes the typing.
  useEffect(() => {
    if (!open) return;
    setTitle(ticket.title);
    setDescription(ticket.description);
    setType(ticket.type);
    setAttachments(ticket.attachments);
  }, [open, setAttachments]);

  if (!open) return null;

  const trimmedTitle = title.trim();
  const trimmedDescription = description.trim();
  const isDirty =
    trimmedTitle !== ticket.title ||
    trimmedDescription !== ticket.description ||
    type !== ticket.type ||
    attachments.length !== ticket.attachments.length ||
    attachments.some((a, i) => a.key !== ticket.attachments[i]?.key);
  const locked = msLeft === 0;
  const canSave = Boolean(
    trimmedTitle && trimmedDescription && type && isDirty && !locked
  );

  const handleSave = () => {
    if (!canSave || savingRef.current) return;
    savingRef.current = true;
    updateTicket.mutate(
      {
        id: ticket.id,
        title: trimmedTitle,
        description: trimmedDescription,
        type: type as SupportTicketType,
        attachments,
      },
      {
        onSuccess: () => {
          toastStore.getState().show({ message: 'Ticket updated' });
          onClose();
        },
        onError: (error) => {
          toastStore.getState().show({
            message: error.message || 'Could not update the ticket',
          });
          onClose();
        },
        onSettled: () => {
          savingRef.current = false;
        },
      }
    );
  };

  return (
    <Modal
      onClose={onClose}
      // The Support drawer underneath also closes on Escape (document
      // listener); stopping it here keeps the drawer and closes only the
      // editor.
      onKeyDown={(e) => {
        if (e.key !== 'Escape') return;
        e.stopPropagation();
        onClose();
      }}
      open={open}
    >
      <ModalHeader
        onClose={onClose}
        title={
          ticket.ticketRef ? `Edit ticket ${ticket.ticketRef}` : 'Edit ticket'
        }
      />
      <div className={css.form} onPaste={onPaste}>
        <Text as="p" role="status" size="xs" variant="muted">
          {locked
            ? 'The edit window has closed. Add anything else as a comment.'
            : formatEditTimeLeft(msLeft)}
        </Text>

        <div className={css.field}>
          <Text as="label" size="sm" weight="medium">
            Title <Required />
          </Text>
          <Input
            maxLength={200}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Short summary of the issue"
            value={title}
          />
        </div>

        <div className={css.field}>
          <Text as="label" size="sm" weight="medium">
            Description <Required />
          </Text>
          <Textarea
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What happened? Steps to reproduce, expected vs actual, and impact."
            rows={4}
            value={description}
          />
        </div>

        <div className={css.field}>
          <Text as="label" size="sm" weight="medium">
            Type <Required />
          </Text>
          <Select
            aria-label="Type"
            onChange={(e) => setType(e.target.value)}
            options={TYPE_OPTIONS}
            placeholder="Select a type"
            usePortal
            value={type}
          />
        </div>

        {uploadsEnabled ? (
          <AttachmentsField
            attachments={attachments}
            isUploading={isUploading}
            onFiles={uploadValidFiles}
            onRemove={removeAttachment}
          />
        ) : null}
      </div>
      <ModalFooter>
        <Button onClick={onClose} variant="ghost">
          Cancel
        </Button>
        <Button
          disabled={!canSave || updateTicket.isPending || isUploading}
          onClick={handleSave}
          variant="primary"
        >
          {updateTicket.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
